import { useEffect, useState } from 'react';
import {
  MenuItem,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../util/fetchOrThrow';
import { usePremiumTokens } from '../theme/usePremiumTokens';

const useStyles = makeStyles()(() => ({
  autocompleteMultiple: {
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-input': {
      minWidth: '1px !important',
    },
    '& .MuiAutocomplete-tag .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

const SelectField = ({
  label,
  fullWidth,
  multiple,
  value = null,
  emptyValue = null,
  emptyTitle = '',
  onChange,
  endpoint,
  data,
  keyGetter = (item) => item.id,
  titleGetter = (item) => item.name,
  helperText,
  disabled,
  placeholder,
  singleLine,
}) => {
  const tokens = usePremiumTokens();
  const { classes } = useStyles();
  const [items, setItems] = useState();

  // Safe token access with fallbacks
  const safeTokens = {
    surface: {
      elevated: tokens?.surface?.elevated || '#161b22',
      glass: tokens?.surface?.glass || 'rgba(255, 255, 255, 0.03)',
    },
    radius: {
      md: tokens?.radius?.md || '8px',
      lg: tokens?.radius?.lg || '12px',
    },
    transitions: {
      all: tokens?.transitions?.all || 'all 0.2s ease',
      colors: tokens?.transitions?.colors || 'color 0.2s ease, border-color 0.2s ease',
    },
    border: {
      default: tokens?.border?.default || 'rgba(255, 255, 255, 0.08)',
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
      hover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
      muted: tokens?.text?.muted || '#6e7681',
    },
    spacing: {
      sm: tokens?.spacing?.[2] || '0.5rem',
    },
  };

  const findOption = (option) => {
    if (typeof option === 'object') {
      return option;
    }
    return items.find((obj) => keyGetter(obj) === option);
  };

  const getOptionLabel = (option) => {
    option = findOption(option);
    return option ? titleGetter(option) : emptyTitle;
  };

  useEffect(() => setItems(data), [data]);

  useEffectAsync(async () => {
    if (endpoint) {
      const response = await fetchOrThrow(endpoint);
      setItems(await response.json());
    }
  }, []);

  // Premium autocomplete dropdown styles
  const autocompleteSlotProps = {
    paper: {
      sx: {
        backgroundColor: 'rgba(22, 27, 34, 0.75)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: safeTokens.radius.lg,
        border: `1px solid ${safeTokens.border.subtle}`,
        mt: 0.5,
        '& .MuiAutocomplete-listbox': {
          maxHeight: 300,
          '& .MuiAutocomplete-option': {
            color: safeTokens.text.primary,
            padding: '10px 16px',
            transition: safeTokens.transitions.colors,
            '&:hover': {
              backgroundColor: safeTokens.accent.subtle,
            },
            '&[aria-selected="true"]': {
              backgroundColor: safeTokens.accent.hover,
              '&:hover': {
                backgroundColor: safeTokens.accent.hover,
              },
            },
          },
        },
        '& .MuiAutocomplete-noOptions': {
          color: safeTokens.text.muted,
          padding: '14px 16px',
        },
        '& .MuiAutocomplete-loading': {
          color: safeTokens.text.muted,
          padding: '14px 16px',
        },
      },
    },
  };

  if (items) {
    const autocompleteValue = multiple
      ? (value || []).map((it) => findOption(it)).filter((it) => it != null)
      : findOption(value) || null;

    return (
      <Autocomplete
        size={singleLine ? 'small' : 'medium'}
        multiple={multiple}
        className={multiple ? classes.autocompleteMultiple : undefined}
        options={items}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          const uniqueKey = keyGetter(option);
          return (
            <MenuItem
              {...rest}
              key={uniqueKey}
              value={uniqueKey}
              sx={{
                color: safeTokens.text.primary,
                '&:hover': {
                  backgroundColor: safeTokens.accent.subtle,
                },
              }}
            >
              {titleGetter(option)}
            </MenuItem>
          );
        }}
        isOptionEqualToValue={(option, selectedOption) => keyGetter(option) === keyGetter(selectedOption)}
        value={autocompleteValue}
        onChange={(_, selectedValue) => {
          if (multiple) {
            onChange({ target: { value: selectedValue.map((item) => keyGetter(item)) } });
          } else {
            onChange({ target: { value: selectedValue ? keyGetter(selectedValue) : emptyValue } });
          }
        }}
        renderValue={multiple ? (tagValue, getItemProps) => {
          if (!tagValue.length) {
            return null;
          }
          return (
            <>
              <Chip
                {...getItemProps({ index: 0 })}
                key={keyGetter(tagValue[0])}
                label={titleGetter(tagValue[0])}
                size="small"
                sx={{ minWidth: 0 }}
              />
              {tagValue.length > 1 && (
                <Chip
                  label={`${tagValue.length - 1}`}
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
              )}
            </>
          );
        } : undefined}
        fullWidth={fullWidth}
        disabled={disabled}
        disableCloseOnSelect={multiple}
        slotProps={autocompleteSlotProps}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            helperText={helperText}
            placeholder={multiple && !autocompleteValue.length ? placeholder : undefined}
            className="premium-input"
            slotProps={{
              inputLabel: {
                ...params.InputLabelProps,
                shrink: multiple && !autocompleteValue.length && Boolean(placeholder) || params.InputLabelProps?.shrink,
              },
            }}
          />
        )}
      />
    );
  }
  return null;
};

export default SelectField;
